import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";

// Stays service page. Frontend-only; mirrors the Flights page structure with
// Stays-specific copy. CTAs route into the existing Plan a Trip intake and
// Track Trip — no new logic or dependencies.

const ARRANGE = [
  { t: "City hotels", d: "Well-located hotels for business or a city break — vetted for comfort and service." },
  { t: "Safari lodges", d: "Tented camps and lodges close to the wildlife, without the guesswork." },
  { t: "Beach resorts", d: "Coastal escapes from Zanzibar to Cape Town, matched to your pace." },
  { t: "Private villas", d: "Whole-home stays with space and privacy for you and yours." },
  { t: "Boutique stays", d: "Characterful, design-led places with a sense of where you are." },
  { t: "Group & family", d: "Connecting rooms, suites and homes that keep everyone together." },
];

const STYLES = [
  "Luxury", "Business", "Family", "Honeymoon", "Safari",
  "Beach", "City break", "Long stay", "Budget-friendly", "Private villa",
];

const STEPS = [
  { n: "01", t: "Tell us your destination", d: "Share where, when and your travel style." },
  { n: "02", t: "We shortlist suitable stays", d: "Hand-picked options that fit your trip and budget." },
  { n: "03", t: "You confirm and pay securely", d: "Final stay, room and price before any payment." },
  { n: "04", t: "We support check-in & changes", d: "Help with arrival, requests and adjustments." },
];

export default function Stays() {
  return (
    <div className="page stays-page">
      <Seo
        title="Stays Worth the Journey — 513Travel"
        description="Find city hotels, safari lodges, beach resorts, private villas and boutique stays matched to your trip, comfort and budget."
        path="/stays"
      />
      {/* ===== Hero ===== */}
      <section className="page-hero hero-split stays-hero">
        <div className="container">
          <div className="hero-split-copy">
            <div className="eyebrow">Stays</div>
            <h1>Stays worth the journey</h1>
            <p className="lede">
              From city hotels to safari lodges, beach resorts and private villas,
              513Travel helps you find stays that fit your trip, your comfort and
              your budget — with support before you arrive.
            </p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn gold">Find a stay</Link>
              <Link to="/plan-a-trip" className="btn outline">Plan a full trip</Link>
            </div>
          </div>
          <figure className="hero-figure">
            <img
              src="/stays-hero.webp"
              alt="Luxury African lodge suite at golden hour with a scenic terrace view"
              loading="eager"
              decoding="async"
            />
          </figure>
        </div>
      </section>

      {/* ===== What we arrange ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">What we arrange</div>
            <h2>Somewhere worth arriving to</h2>
            <p>Every kind of stay, vetted and matched to how you like to travel — not just a list of rooms.</p>
          </div>
          <div className="grid grid-3">
            {ARRANGE.map((c) => (
              <div className="service-card" key={c.t}>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Stay styles ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">The right fit</div>
            <h2>Stay styles we understand</h2>
            <p>We match the stay to the traveller — the mood, the moment and the people you're with — not just a booking.</p>
          </div>
          <div className="route-chips">
            {STYLES.map((s) => (
              <span className="route-chip" key={s}>{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2>How stay requests work</h2>
          </div>
          <div className="grid grid-4">
            {STEPS.map((s) => (
              <div className="step-card" key={s.n}>
                <span className="step-num" aria-hidden="true">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Trust note ===== */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <p className="demo-note demo-note--center">
            Hotel availability and rates can change quickly. We confirm the final
            stay, room type, inclusions and price before payment.
          </p>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="section section--green">
        <div className="container">
          <div className="feature-copy feature-solo">
            <div className="eyebrow">Ready to stay</div>
            <h2>Ready to find the right stay?</h2>
            <p>Share your destination, dates and travel style. We'll prepare suitable stay options.</p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn light">Find a stay</Link>
              <Link to="/track-trip" className="btn outline-light">Track existing trip</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
